<script setup>
import Layout from "@/layouts/main.vue";
import PageHeader from "@/components/page-header";
import Multiselect from '@suadelabs/vue3-multiselect';
import "@suadelabs/vue3-multiselect/dist/vue3-multiselect.css";
import { required, email, helpers, requiredIf } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { computed, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import axios from "axios";
import { isEqual, cloneDeep } from 'lodash';
import { authHeader } from "../../../../helpers/authservice/auth-header";

const route = useRoute();
const state = ref({
    username: '',
    email: '',
    name: '',
    image: '',
    phone_number: '',
    signature: '',
    roles: null,
    lgus: null
});

const cloneState = ref({
    username: '',
    email: '',
    name: '',
    image: '',
    phone_number: '',
    signature: '',
    roles: null,
    lgus: null
});

const resetState = () => {
    submitted.value = false;
    state.value = {
        username: '',
        email: '',
        name: '',
        image: '',
        phone_number: '',
        signature: '',
        roles: null,
        lgus: null
    }
}

const resetCloneState = () => {
    cloneState.value = {
        username: '',
        email: '',
        name: '',
        image: '',
        phone_number: '',
        signature: '',
        roles: null,
        lgus: null
    }
}
      
const loading = computed(() => store.state.globals.loading);

const rules = computed(() => ({
    name: {
        required: helpers.withMessage("Full Name is required", required),
    },
    phone_number: {
        required: helpers.withMessage("Phone Number is required", required),
    },
    username: {
        requiredIfFunction: helpers.withMessage("Username is required when Email is not provided",
            requiredIf(() => !cloneState.value.email)),
    },
    email: {
        requiredIfFunction: helpers.withMessage("Email is required when Username is not provided",
            requiredIf(() => !cloneState.value.username)),
        email: helpers.withMessage("Please enter valid email", email),
    },
    lgus: {
        required: helpers.withMessage("Please select at least one LGU", required),
    },
    roles: {
        required: helpers.withMessage("Please select at least one role", required),
    },
}));

const store = useStore();
const v$ = useVuelidate(rules, cloneState);

const title = ref("Edit User");
const items = ref([
    {
    text: "Home",
    href: "/",
},
    {
    text: "User Management",
    href: "/user-management/users/list",
    active: true
},
{
    text: "Edit User",
    active: true,
}
]);

const getSingleUserDetails = async () => {
    try {
        const { data } = await axios.get(`/users/${route.params.id}`, {
            headers: authHeader()
        });
        state.value = data.user;
        cloneState.value = cloneDeep(state.value);

        if (cloneState.value.image) {
            const previewUserFile = document.querySelectorAll(".user-profile-image")[0];
            previewUserFile.src = cloneState.value.image;
        }

        if (cloneState.value.signature) {
            const previewUserSignatureFile = document.querySelectorAll(".user-profile-image")[1];
            previewUserSignatureFile.src = cloneState.value.signature;
        }
    } catch (err) {
        console.log(err);
    }
}

const authUser = JSON.parse(localStorage.getItem('user'));
const authUserLguIds = new Set((authUser?.lgus ?? []).map(l => l.id));
const isSuperAdmin = (authUser?.roles ?? []).some(r => r.is_superadmin === true);

const roleOptions = computed(() => {
    const all = store ? store.state.role.dropdownRoles : [];
    if (isSuperAdmin) return all;
    return all.filter(r =>  (r.user_type === authUser?.roles?.[0].user_type && r.is_superadmin === false));
});
const lguOptions = computed(() => {
    const all = store ? store.state.lgu.dropdownLGUs : [];
    return all.filter(l => authUserLguIds.has(l.id));
});
const submitted = ref(false);
const notification = computed(() => store ? store.state.notification : null)

function updateUser() {
    if (isEqual(state.value, cloneState.value)) return;

    submitted.value = true
    v$.value.$touch();

    if (v$.value.$invalid) return;

    if (cloneState.value.email === '') {
        delete cloneState.value.email;
    }

    if (cloneState.value.username === '') {
        delete cloneState.value.username;
    }

    if (typeof cloneState.value.image === 'string') {
        delete cloneState.value.image;
    }

    if (typeof cloneState.value.signature === 'string') {
        delete cloneState.value.signature;
    }
    const payload = { ...cloneState.value };
    if (payload.roles && !Array.isArray(payload.roles)) {
        payload.roles = [payload.roles];
    }
    store.dispatch('user/updateUser', payload);

    state.value = cloneDeep(cloneState.value);
}

function getAllRoles() {
    store.dispatch('role/getDropdownRoles')
}

function getAllLGUs() {
    store.dispatch('lgu/getDropdownLGUs')
}

const clearNotification = () => {
    store.dispatch('notification/clear', null, { root: true });
}

const handleUserFileUpload = (event) => {
    const preview = document.querySelectorAll(".user-profile-image")[0];
    const file = event.target.files[0];
    const reader = new FileReader();

    reader.addEventListener(
        "load",
        function () {
            preview.src = reader.result;
                cloneState.value.image = file;
        },
        false
    );

    if (file) {
        reader.readAsDataURL(file);
    }

    clearNotification();
}

const handleUserSignatureFileUpload = (event) => {
    const preview = document.querySelectorAll(".user-profile-image")[1];
    const file = event.target.files[0];
    const reader = new FileReader();

    reader.addEventListener(
        "load",
        function () {
            preview.src = reader.result;
                cloneState.value.signature = file;
        },
        false
    );

    if (file) {
        reader.readAsDataURL(file);
    }

    clearNotification();
}

onMounted(() => {
    clearNotification();
    resetState();
    resetCloneState();
    getSingleUserDetails();
    getAllRoles();
    getAllLGUs();
})
</script>

<template>
  <Layout>
    <PageHeader back="/user-management/users/list" :title="title" :items="items" />
    <b-overlay :show="loading">
        <div class="row">
            <div class="col lg-12">
                <div class="card">
                    <div class="card-header">
                        <h4 class="card-title mb-0">Edit User</h4>
                    </div>
                    <div class="card-body">
                        <b-alert v-if="notification && notification.message"
                            show variant="success" class="fade show" role="alert">
                            <span>{{ notification.message }}</span>
                        </b-alert>

                        <div v-if="notification && notification.messages.length > 0">
                            <b-alert v-for="(message, index) in notification.messages" :key="index"
                                show variant="danger" class="fade show" role="alert">
                                {{ message }}
                            </b-alert>
                        </div>

                        <form @submit.prevent="updateUser" class="mx-2">
                            <div class="text-center">
                                <div class="profile-user position-relative d-inline-block mx-auto mb-2">
                                    <img src="@/assets/images/users/user-dummy-img.jpg" class="rounded-circle avatar-lg img-thumbnail user-profile-image" alt="user-signature-image">
                                    <div class="avatar-xs p-0 rounded-circle profile-photo-edit">
                                        <input id="profile-img-file-input" @change="handleUserFileUpload" type="file" class="profile-img-file-input" accept="image/png, image/jpeg">
                                        <label for="profile-img-file-input" class="profile-photo-edit avatar-xs">
                                            <span class="avatar-title rounded-circle bg-light text-body">
                                                <i class="ri-camera-fill"></i>
                                            </span>
                                        </label>
                                    </div>
                                </div>
                                <h5 class="fs-14">Add User Image</h5>
                            </div>

                            <div class="row">
                                <p class="fs-6 text text-decoration-underline">User Information</p>
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="name" class="form-label required">Full Name</label> 
                                        <input 
                                            :class="{ 'is-invalid': submitted && v$.name.$error }"
                                            @input="clearNotification"
                                            v-model="cloneState.name" type="text" class="form-control" placeholder="Enter full name..." id="name" />
                                        <div
                                            v-for="item of v$.name.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="phoneInput" class="form-label required">Phone Number</label> 
                                        <input 
                                            @input="clearNotification"
                                            v-maska="['+63 (###) ### ####', '+63 (###) #### ####']"
                                            :class="{ 'is-invalid': submitted && v$.phone_number.$error }"
                                            v-model="cloneState.phone_number" type="text" class="form-control" placeholder="Enter phone number..." id="phoneInput">
                                        <div class="text-muted">e.g +63 (912) 345 6789 / +63 (032) 8123-4567</div>
                                        <div
                                            v-for="item of v$.phone_number.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="usernameInput" class="form-label">Username</label>
                                        <input
                                            @input="clearNotification"
                                            :class="{ 'is-invalid': submitted && v$.username.$error }"
                                            v-model="cloneState.username" type="text" class="form-control" placeholder="Enter username..." id="usernameInput">
                                        <div
                                            v-for="item of v$.username.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="email" class="form-label">Email Address</label>
                                        <input 
                                            @input="clearNotification"
                                            :class="{ 'is-invalid': submitted && v$.email.$error }"
                                            v-model="cloneState.email" type="email" class="form-control" placeholder="Enter email address..." id="email">
                                        <div
                                            v-for="item of v$.email.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="rolesInput" class="form-label required">Roles</label>
                                        <multiselect
                                            @input="clearNotification"
                                            :class="{ 'is-invalid': submitted && v$.roles.$error }"
                                            v-model="cloneState.roles"
                                            :close-on-select="true"
                                            :options="roleOptions"
                                            placeholder="Select Role(s)"
                                            label="name"
                                            track-by="name"
                                        />
                                        <div
                                            v-for="item of v$.roles.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="lguInput" class="form-label required">LGU Assign</label>
                                        <multiselect
                                            @input="clearNotification"
                                            :class="{ 'is-invalid': submitted && v$.lgus.$error }"
                                            :multiple="true"
                                            v-model="cloneState.lgus"
                                            :close-on-select="true"
                                            :options="lguOptions"
                                            placeholder="Select LGU Assign"
                                            label="name"
                                            track-by="name"
                                        />
                                        <div
                                            v-for="item of v$.lgus.$errors"
                                            :key="item.$uid"
                                            class="invalid-feedback"
                                        >
                                            <span v-if="item.$message">{{ item.$message }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-6">
                                    <div class="mb-3">
                                        <label for="eSignatureInput" class="form-label d-block">e-Signature</label> 
                                        <div class="profile-user position-relative d-inline-block mx-auto mb-2">
                                            <img src="@/assets/images/users/signature-img.jpeg" class="rounded-circle avatar-lg img-thumbnail user-profile-image" alt="user-profile-image">
                                            <div class="avatar-xs p-0 rounded-circle profile-photo-edit">
                                                <input id="profile-sig-file-input" @change="handleUserSignatureFileUpload" type="file" class="profile-img-file-input" accept="image/png, image/jpeg">
                                                <label for="profile-sig-file-input" class="profile-photo-edit avatar-xs">
                                                    <span class="avatar-title rounded-circle bg-light text-body">
                                                        <i class="ri-camera-fill"></i>
                                                    </span>
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="row">
                                <div class="col-lg-12">
                                    <div class="text-end">
                                        <router-link to="/user-management/users/list" type="submit" class="btn btn-danger mr-3">
                                            <i class="ri-close-fill fs-4 mr-1 valign-middle"></i>
                                            Cancel
                                        </router-link>
                                        <button :disabled="isEqual(state, cloneState)" type="submit" class="btn btn-success">
                                            <i class="ri-save-2-fill fs-4 mr-1 valign-middle"></i>
                                            Update User
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    </b-overlay>
  </Layout>
</template>